import { useEffect, useRef, useState } from 'react';
import { ApiFailure, api } from '../lib/api';
import { X } from 'lucide-react';
import { InlineIcon } from './InlineIcon';

interface Inbound {
  statusPath?: string;
  eventIdPath?: string;
  channelOrderNoPath?: string;
  orderRefPath?: string;
  map?: Record<string, { action: string }>;
  verify?: { type?: string; signHeader?: string; timestampHeader?: string; toleranceSec?: number };
}

interface TestResult {
  extracted: Record<string, unknown>;
  missing: string[];
  action: string | null;
  problems: string[];
  ok: boolean;
}

const ACTIONS: Array<{ value: string; label: string }> = [
  { value: 'ACCEPT', label: '对方已接单' },
  { value: 'PICKUP', label: '已取件' },
  { value: 'START_TRANSIT', label: '配送中' },
  { value: 'DELIVER', label: '已送达' },
  { value: 'FAIL', label: '投递失败' },
  { value: 'CANCEL', label: '已取消' },
];

const SAMPLE = `{
  "eventId": "evt_123",
  "status": "COMPLETED",
  "waybillNo": "WB-000001",
  "timestamp": 1767225600
}`;

/**
 * 回调配置。
 *
 * ★ 两件事必须在这一屏解决,否则整套回调等于没做:
 *
 *   1. **把回调地址给出来** —— 它得被填进对方的后台。藏在数据库里没人拿得到
 *   2. **配完能当场验** —— 把对方文档里的样例报文贴进来,立刻看到
 *      「我们会从中读出什么、会执行哪个动作」。
 *      等真回调打进来才发现路径写错,那批单已经卡住了,
 *      而对方那边显示的是「我已经通知你们了」
 */
export function InboundConfig({
  channelId,
  channelName,
  callbackUrl,
  existing,
  credentialHint,
  onClose,
  onSaved,
}: {
  channelId: string;
  channelName: string;
  callbackUrl: string | null;
  existing?: Inbound | null;
  credentialHint?: string;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [cfg, setCfg] = useState<Inbound>({
    statusPath: existing?.statusPath ?? 'body.status',
    eventIdPath: existing?.eventIdPath ?? 'body.eventId',
    channelOrderNoPath: existing?.channelOrderNoPath ?? 'body.waybillNo',
    orderRefPath: existing?.orderRefPath ?? '',
    map: existing?.map ?? { COMPLETED: { action: 'DELIVER' } },
    verify: existing?.verify ?? { type: 'NONE' },
  });
  const [sample, setSample] = useState(SAMPLE);
  const [result, setResult] = useState<TestResult | null>(null);
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 边改边试,防抖 —— 每敲一个字符打一次接口既慢又吵
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      void api<TestResult>(`/integrations/channels/${channelId}/inbound/test`, {
        method: 'POST',
        body: { sample, inbound: normalize(cfg) },
      })
        .then(setResult)
        .catch((e) => setError(e instanceof ApiFailure ? e.message : '试不通'));
    }, 400);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [channelId, sample, cfg]);

  async function save() {
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      await api(`/integrations/channels/${channelId}/inbound`, {
        method: 'PUT',
        body: normalize(cfg),
      });
      await onSaved();
      onClose();
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : '保存失败');
    } finally {
      setSaving(false);
    }
  }

  const rows = Object.entries(cfg.map ?? {});

  return (
    <div className="fixed inset-0 z-[70] grid place-items-center bg-black/50 p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="回调配置"
        className="w-full max-w-5xl max-h-[92dvh] overflow-y-auto rounded-2xl bg-white
                   dark:bg-slate-800 p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-1 flex items-baseline justify-between">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            回调配置 · {channelName}
          </h3>
          <button onClick={onClose} aria-label="关闭" className="text-slate-400 text-xl">
            <InlineIcon icon={X} />
          </button>
        </div>
        <p className="mb-4 text-sm text-slate-500">
          对方派送到哪一步,靠回调告诉我们。不配的话,单子推出去就断了线。
        </p>

        {error && (
          <div className="mb-3 rounded-lg bg-red-50 dark:bg-red-950 border border-red-200
                          dark:border-red-900 px-3 py-2 text-sm text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        {/* ★ 回调地址放最上面 —— 这是要填进对方后台的东西 */}
        <section className="mb-4 rounded-xl border border-brand-200 dark:border-brand-800
                            bg-brand-50/60 dark:bg-brand-500/10 p-3">
          <div className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            把这个地址填进对方的后台
          </div>
          <div className="flex gap-2">
            <input
              aria-label="回调地址"
              readOnly
              value={callbackUrl ?? '（这条通道还没有回调地址）'}
              className="flex-1 h-10 px-3 rounded-lg border border-slate-300 dark:border-slate-600
                         bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100
                         text-xs font-mono"
            />
            <button
              onClick={() => {
                if (!callbackUrl) return;
                void navigator.clipboard.writeText(callbackUrl).then(() => {
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                });
              }}
              className="h-10 px-4 rounded-lg bg-brand-600 text-white text-sm font-medium"
            >
              {copied ? '已复制' : '复制'}
            </button>
          </div>
          {/* 地址里带着密钥 —— 说清楚,免得有人贴进工单系统 */}
          <p className="mt-1 text-xs text-slate-500">
            地址末尾那段是密钥,等同于凭据。只贴给对方的对接人,别放进公开的工单或群里。
          </p>
        </section>

        <div className="grid gap-4 lg:grid-cols-2">
          {/* ── 左:配置 ── */}
          <div className="space-y-3">
            <section>
              <h4 className="mb-1.5 text-sm font-medium text-slate-700 dark:text-slate-300">
                字段在报文里的位置
              </h4>
              <div className="space-y-2">
                <PathField
                  label="状态"
                  hint="对方在报什么"
                  value={cfg.statusPath ?? ''}
                  onChange={(v) => setCfg({ ...cfg, statusPath: v })}
                />
                <PathField
                  label="事件 ID"
                  hint="去重靠它 —— 不配的话对方重发我们会重复处理"
                  value={cfg.eventIdPath ?? ''}
                  onChange={(v) => setCfg({ ...cfg, eventIdPath: v })}
                />
                <PathField
                  label="三方单号"
                  hint="按它找回是我们哪一单"
                  value={cfg.channelOrderNoPath ?? ''}
                  onChange={(v) => setCfg({ ...cfg, channelOrderNoPath: v })}
                />
                <PathField
                  label="我方单号"
                  hint="对方回传我们的单号时用(可不填)"
                  value={cfg.orderRefPath ?? ''}
                  onChange={(v) => setCfg({ ...cfg, orderRefPath: v })}
                />
              </div>
            </section>

            <section>
              <h4 className="mb-1.5 text-sm font-medium text-slate-700 dark:text-slate-300">
                状态映射
                <span className="ml-2 font-normal text-xs text-slate-400">
                  没列出来的状态会被忽略 —— 对方的状态比我们关心的多得多
                </span>
              </h4>
              <div className="space-y-1.5">
                {rows.map(([status, v]) => (
                  <div key={status} className="flex gap-2 items-center">
                    <input
                      aria-label={`三方状态 ${status}`}
                      value={status}
                      onChange={(e) => renameKey(cfg, setCfg, status, e.target.value)}
                      className="flex-1 h-9 px-2 rounded-lg border border-slate-300
                                 dark:border-slate-600 bg-white dark:bg-slate-900
                                 text-slate-900 dark:text-slate-100 text-xs font-mono"
                    />
                    <span className="text-slate-400 text-xs">→</span>
                    <select
                      aria-label={`${status} 对应动作`}
                      value={v.action}
                      onChange={(e) =>
                        setCfg({
                          ...cfg,
                          map: { ...cfg.map, [status]: { action: e.target.value } },
                        })
                      }
                      className="flex-1 h-9 px-2 rounded-lg border border-slate-300
                                 dark:border-slate-600 bg-white dark:bg-slate-900
                                 text-slate-900 dark:text-slate-100 text-xs"
                    >
                      {ACTIONS.map((a) => (
                        <option key={a.value} value={a.value}>
                          {a.label}
                        </option>
                      ))}
                    </select>
                    <button
                      aria-label={`删除映射 ${status}`}
                      onClick={() => {
                        const next = { ...cfg.map };
                        delete next[status];
                        setCfg({ ...cfg, map: next });
                      }}
                      className="text-slate-400 px-1"
                    >
                      <InlineIcon icon={X} />
                    </button>
                  </div>
                ))}
                <button
                  onClick={() =>
                    setCfg({
                      ...cfg,
                      map: { ...cfg.map, [`NEW_${rows.length + 1}`]: { action: 'DELIVER' } },
                    })
                  }
                  className="text-sm text-brand-600 dark:text-brand-500"
                >
                  + 加一条映射
                </button>
              </div>
            </section>

            <section>
              <h4 className="mb-1.5 text-sm font-medium text-slate-700 dark:text-slate-300">
                验签
              </h4>
              <select
                aria-label="验签方式"
                value={cfg.verify?.type ?? 'NONE'}
                onChange={(e) =>
                  setCfg({ ...cfg, verify: { ...cfg.verify, type: e.target.value } })
                }
                className="w-full h-10 px-3 rounded-lg border border-slate-300 dark:border-slate-600
                           bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 text-sm"
              >
                <option value="NONE">不验签(只靠地址里的密钥)</option>
                <option value="HMAC">HMAC 签名</option>
              </select>
              {cfg.verify?.type === 'HMAC' && (
                <div className="mt-2 space-y-2">
                  <PathField
                    label="签名头"
                    hint="对方把签名放在哪个 header"
                    value={cfg.verify.signHeader ?? 'X-Signature'}
                    onChange={(v) => setCfg({ ...cfg, verify: { ...cfg.verify, signHeader: v } })}
                  />
                  <PathField
                    label="时间戳头"
                    hint="填了才做重放检查 —— 签名对但十天前的报文照样该拒"
                    value={cfg.verify.timestampHeader ?? ''}
                    onChange={(v) =>
                      setCfg({ ...cfg, verify: { ...cfg.verify, timestampHeader: v } })
                    }
                  />
                  {credentialHint && (
                    <p className="text-xs text-amber-700 dark:text-amber-400">{credentialHint}</p>
                  )}
                </div>
              )}
            </section>
          </div>

          {/* ── 右:拿样例试 ── */}
          <div className="space-y-3">
            <label className="block">
              <span className="block mb-1 text-sm text-slate-600 dark:text-slate-400">
                贴一份对方文档里的样例报文
              </span>
              <textarea
                aria-label="样例报文"
                value={sample}
                onChange={(e) => setSample(e.target.value)}
                spellCheck={false}
                rows={10}
                className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600
                           bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100
                           font-mono text-xs"
              />
            </label>

            <div
              aria-label="试算结果"
              className="rounded-xl border border-slate-200 dark:border-slate-700 p-3 space-y-2"
            >
              <div className="text-sm">
                <span className="text-slate-500">收到这条会:</span>{' '}
                {result?.action ? (
                  <span className="text-green-700 dark:text-green-400 font-medium">
                    {ACTIONS.find((a) => a.value === result.action)?.label ?? result.action}
                  </span>
                ) : (
                  <span className="text-amber-700 dark:text-amber-400">忽略(没有对应动作)</span>
                )}
              </div>

              {result && Object.keys(result.extracted).length > 0 && (
                <div className="text-xs font-mono text-slate-600 dark:text-slate-400 space-y-0.5">
                  {Object.entries(result.extracted).map(([k, v]) => (
                    <div key={k}>
                      {k}: <span className="text-slate-900 dark:text-slate-200">{String(v)}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* ★ 每条问题都给一句能照着做的话 —— 只说「取不到」的话,
                     配的人不知道是路径写错了还是样例里本来就没有 */}
              {result && result.problems.length > 0 && (
                <ul className="text-xs text-amber-700 dark:text-amber-400 space-y-0.5">
                  {result.problems.map((p) => (
                    <li key={p}>· {p}</li>
                  ))}
                </ul>
              )}

              {result?.ok && (
                <p className="text-xs text-green-700 dark:text-green-400">配置看起来是齐的。</p>
              )}
            </div>
          </div>
        </div>

        <div className="mt-4 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 h-10 rounded-lg border border-slate-300 dark:border-slate-600
                       text-slate-700 dark:text-slate-300"
          >
            取消
          </button>
          <button
            onClick={() => void save()}
            disabled={saving}
            className="flex-1 h-10 rounded-lg bg-brand-600 text-white font-medium disabled:opacity-40"
          >
            {saving ? '保存中…' : '保存回调配置'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** 空字符串不往后端传 —— 传了会被当成「配了一个取不到东西的路径」 */
function normalize(cfg: Inbound): Record<string, unknown> {
  const out: Record<string, unknown> = { map: cfg.map ?? {}, ignoreUnmapped: true };
  for (const k of ['statusPath', 'eventIdPath', 'channelOrderNoPath', 'orderRefPath'] as const) {
    const v = cfg[k];
    if (v && v.trim()) out[k] = v.trim();
  }
  if (cfg.verify?.type && cfg.verify.type !== 'NONE') {
    out['verify'] = {
      type: cfg.verify.type,
      ...(cfg.verify.signHeader ? { signHeader: cfg.verify.signHeader } : {}),
      ...(cfg.verify.timestampHeader ? { timestampHeader: cfg.verify.timestampHeader } : {}),
    };
  }
  return out;
}

/** 改键名要保序,否则每敲一个字符行就跳一次位置 */
function renameKey(
  cfg: Inbound,
  setCfg: (c: Inbound) => void,
  from: string,
  to: string,
): void {
  const next: Record<string, { action: string }> = {};
  for (const [k, v] of Object.entries(cfg.map ?? {})) next[k === from ? to : k] = v;
  setCfg({ ...cfg, map: next });
}

function PathField({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="block">
      <span className="text-xs text-slate-600 dark:text-slate-400">
        {label}
        <span className="text-slate-400"> —— {hint}</span>
      </span>
      <input
        aria-label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        spellCheck={false}
        className="mt-0.5 w-full h-9 px-2 rounded-lg border border-slate-300 dark:border-slate-600
                   bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 text-xs font-mono"
      />
    </label>
  );
}
